var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Import the Speech SDK from the npm package
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
function sendTranscript(params) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield fetch(`${params.smartclaimUrl}/transcript?access_token=${encodeURIComponent(params.smartclaimAccessToken)}`, {
            method: 'POST',
            body: JSON.stringify({
                transcript: params.transcript,
                meeting_id: params.meetingId
            }),
            headers: {
                'Authorization': `Bearer ${params.smartclaimAccessToken}`,
                'Content-Type': 'application/json'
            }
        });
        const responseData = yield response.json();
        return {
            message: responseData,
            request_id: params.meetingId,
            type: 'response',
            sub_type: 'transcript_processed',
            key: 'transcript_processed'
        };
    });
}
// Standalone streamTranscribe function for audio streaming and transcription
export function streamTranscribe(params) {
    return __awaiter(this, void 0, void 0, function* () {
        // Generate meeting ID if not provided
        const meetingId = params.meetingId || generateUUID();
        // Set default configurations
        const language = params.language || 'en-US';
        const targetSampleRate = params.sampleRate || 16000;
        const audioGain = params.gain || 2.0; // Default 2x gain for better transcription
        let micStream = null;
        let sysStream = null;
        let audioContext = null;
        let recognizer = null;
        let isStreaming = false;
        // Create a request ID for messages
        try {
            // 1. Acquire microphone stream with optional device ID
            const micConstraints = {
                audio: params.microphoneDeviceId
                    ? { deviceId: { exact: params.microphoneDeviceId } }
                    : true
            };
            micStream = yield navigator.mediaDevices.getUserMedia(micConstraints);
            // Check if microphone stream has audio tracks (Firefox compatibility)
            if (micStream.getAudioTracks().length === 0) {
                throw new Error("No audio tracks found in microphone stream");
            }
            // 2. Optionally acquire system audio if requested
            if (params.includeSystemAudio) {
                try {
                    sysStream = yield navigator.mediaDevices.getDisplayMedia({ audio: true });
                    // Check if system audio stream has audio tracks (Firefox compatibility)
                    if (sysStream.getAudioTracks().length === 0) {
                        if (params.onEvent) {
                            params.onEvent({
                                message: {
                                    status_update: 'System audio stream has no audio tracks (Firefox issue)',
                                },
                                request_id: meetingId,
                                type: 'warning',
                                sub_type: 'system_audio',
                                key: 'system_audio_no_tracks'
                            });
                        }
                        // Set to null to avoid using a stream without audio tracks
                        sysStream = null;
                    }
                    else {
                        // Notify that system audio was successfully acquired
                        if (params.onEvent) {
                            params.onEvent({
                                message: {
                                    status_update: 'System audio capture started',
                                },
                                request_id: meetingId,
                                type: 'info',
                                sub_type: 'system_audio',
                                key: 'system_audio_started'
                            });
                        }
                    }
                }
                catch (err) {
                    // Handle case where system audio permission is denied
                    if (params.onEvent) {
                        params.onEvent({
                            message: {
                                status_update: `System audio capture failed: ${err.message}`,
                            },
                            request_id: meetingId,
                            type: 'warning',
                            sub_type: 'system_audio',
                            key: 'system_audio_failed'
                        });
                    }
                    // Continue with just microphone audio
                    sysStream = null;
                }
            }
            // 3. Create AudioContext without specifying sample rate to match the stream's native rate
            audioContext = new AudioContext();
            const actualSampleRate = audioContext.sampleRate;
            if (params.onEvent) {
                params.onEvent({
                    message: {
                        status_update: `Using native browser sample rate: ${actualSampleRate}Hz, will downsample to ${targetSampleRate}Hz`,
                    },
                    request_id: meetingId,
                    type: 'info',
                    sub_type: 'audio',
                    key: 'sample_rate_info'
                });
            }
            // Notify about gain setting
            if (params.onEvent) {
                params.onEvent({
                    message: {
                        status_update: `Audio gain set to ${audioGain}x for enhanced transcription quality`,
                    },
                    request_id: meetingId,
                    type: 'info',
                    sub_type: 'audio',
                    key: 'audio_gain_info'
                });
            }
            // Create nodes for mic and system audio
            const micNode = audioContext.createMediaStreamSource(micStream);
            // Set up merger node (mono or stereo depending on if we have system audio)
            const merger = audioContext.createChannelMerger(sysStream ? 2 : 1);
            // Connect mic to first channel
            micNode.connect(merger, 0, 0);
            // Connect system audio to second channel if available
            let sysNode = null;
            if (sysStream && sysStream.getAudioTracks().length > 0) {
                try {
                    sysNode = audioContext.createMediaStreamSource(sysStream);
                    sysNode.connect(merger, 0, sysStream ? 1 : 0);
                }
                catch (error) {
                    if (params.onEvent) {
                        params.onEvent({
                            message: {
                                status_update: `Failed to create system audio source: ${error.message}`,
                            },
                            request_id: meetingId,
                            type: 'warning',
                            sub_type: 'system_audio',
                            key: 'system_audio_source_failed'
                        });
                    }
                    // Fall back to microphone only
                }
            }
            // 4. Set up Azure Speech SDK
            // Create push stream for audio data
            const pushStream = SpeechSDK.AudioInputStream.createPushStream();
            // Create audio config from push stream
            const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(pushStream);
            // Create speech config from authorization token
            const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(params.azureSpeechToken, params.region);
            // Set speech recognition language
            speechConfig.speechRecognitionLanguage = language;
            // Enable detailed recognition results
            speechConfig.outputFormat = SpeechSDK.OutputFormat.Detailed;
            // Create speech recognizer
            recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
            // 5. Set up event handlers for the recognizer
            // Add flag to track if recognition_started has been emitted
            let recognitionStartedEmitted = false;
            let recognitionCanceled = false;
            recognizer.recognized = (sender, event) => {
                if (event.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
                    const transcript = event.result.text;
                    // Call onTranscript callback if provided
                    if (params.onTranscript) {
                        params.onTranscript(transcript, true);
                    }
                    // Send event message
                    if (params.onEvent) {
                        params.onEvent({
                            message: {
                                transcript: transcript,
                            },
                            request_id: meetingId,
                            type: 'response',
                            sub_type: 'transcription',
                            key: 'transcription'
                        });
                    }
                    // Send transcript to SmartClaim backend
                    sendTranscript({
                        transcript,
                        smartclaimAccessToken: params.smartclaimAccessToken,
                        meetingId,
                        smartclaimUrl: params.smartclaimUrl
                    }).then(response => {
                        // Emit the response from sendTranscript as a new event
                        if (params.onEvent) {
                            params.onEvent(response);
                        }
                    }).catch(error => {
                        if (params.onError) {
                            params.onError(new Error(`Failed to send transcript: ${error.message}`));
                        }
                    });
                }
            };
            recognizer.recognizing = (sender, event) => {
                if (event.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
                    const transcript = event.result.text;
                    // Emit recognition_started only if not canceled and not already emitted
                    if (!recognitionStartedEmitted && !recognitionCanceled) {
                        recognitionStartedEmitted = true;
                        if (params.onEvent) {
                            params.onEvent({
                                message: {
                                    status_update: 'Azure Speech recognition started and ready to transcribe',
                                },
                                request_id: meetingId,
                                type: 'info',
                                sub_type: 'recognition',
                                key: 'recognition_started'
                            });
                        }
                    }
                    // Call onTranscript callback with interim results
                    if (params.onTranscript) {
                        params.onTranscript(transcript, false);
                    }
                    // Optionally send interim results as events
                    if (params.onEvent) {
                        params.onEvent({
                            message: {
                                transcript,
                            },
                            request_id: meetingId,
                            type: 'interim',
                            sub_type: 'transcription',
                            key: 'interim_transcription'
                        });
                    }
                }
            };
            recognizer.canceled = (sender, event) => {
                recognitionCanceled = true;
                if (event.reason === SpeechSDK.CancellationReason.Error) {
                    if (params.onError) {
                        params.onError(new Error(`Recognition canceled: ${event.errorDetails}`));
                    }
                }
            };
            // 6. Set up audio processing
            const bufferSize = 4096;
            const processorNode = audioContext.createScriptProcessor(bufferSize, sysStream ? 2 : 1, 1);
            // Helper functions for downsampling and conversion
            function downsampleBuffer(input, inputSampleRate, outputSampleRate) {
                if (inputSampleRate === outputSampleRate) {
                    return input;
                }
                const ratio = inputSampleRate / outputSampleRate;
                const newLength = Math.round(input.length / ratio);
                const result = new Float32Array(newLength);
                for (let i = 0; i < newLength; i++) {
                    result[i] = input[Math.floor(i * ratio)];
                }
                return result;
            }
            processorNode.onaudioprocess = (e) => {
                if (!isStreaming)
                    return;
                // Get channel data
                const input0 = e.inputBuffer.getChannelData(0); // Microphone
                // If we have system audio, mix it with mic
                let input1 = new Float32Array(bufferSize).fill(0);
                if (sysStream && e.inputBuffer.numberOfChannels > 1) {
                    input1 = e.inputBuffer.getChannelData(1); // System audio
                }
                // Mix the channels
                const mixedBuffer = new Float32Array(input0.length);
                for (let i = 0; i < input0.length; i++) {
                    mixedBuffer[i] = (input0[i] + (sysStream ? input1[i] : 0)) * 0.5;
                }
                // Downsample if needed
                const downsampledBuffer = downsampleBuffer(mixedBuffer, actualSampleRate, targetSampleRate);
                // Apply gain
                const gainBuffer = new Float32Array(downsampledBuffer.length);
                for (let i = 0; i < downsampledBuffer.length; i++) {
                    gainBuffer[i] = downsampledBuffer[i] * audioGain;
                }
                // Convert to Int16 (required by Azure Speech SDK)
                const monoInt16 = new Int16Array(gainBuffer.length);
                for (let i = 0; i < gainBuffer.length; i++) {
                    // Clip to [-1, 1] range and convert to Int16
                    monoInt16[i] = Math.max(-1, Math.min(1, gainBuffer[i])) * 0x7FFF;
                }
                // Write to the push stream
                pushStream.write(monoInt16.buffer);
            };
            // Connect the processor node
            merger.connect(processorNode);
            processorNode.connect(audioContext.destination);
            // 7. Start continuous recognition
            isStreaming = true;
            recognizer.startContinuousRecognitionAsync(() => {
                // Recognition initialized - now we wait for first transcription before saying "started"
                if (params.onEvent && !recognitionCanceled) {
                    params.onEvent({
                        message: {
                            status_update: 'Azure Speech recognition initialized, waiting for audio...',
                        },
                        request_id: meetingId,
                        type: 'info',
                        sub_type: 'recognition',
                        key: 'recognition_initialized'
                    });
                }
                // Add timeout to notify if recognition doesn't start receiving audio
                setTimeout(() => {
                    if (!recognitionStartedEmitted && !recognitionCanceled) {
                        if (params.onEvent) {
                            params.onEvent({
                                message: {
                                    status_update: 'Audio processing ready but no speech detected yet',
                                },
                                request_id: meetingId,
                                type: 'debug',
                                sub_type: 'recognition',
                                key: 'recognition_ready'
                            });
                        }
                    }
                }, 5000); // 5 second timeout
            }, (error) => {
                // Recognition failed to start
                isStreaming = false;
                // Check if the error is related to authentication (token expired)
                const errorMessage = String(error);
                const isAuthError = errorMessage.includes('token') ||
                    errorMessage.includes('auth') ||
                    errorMessage.includes('unauthorized') ||
                    errorMessage.includes('expired');
                // Emit error event
                if (params.onEvent) {
                    params.onEvent({
                        message: {
                            status_update: `Recognition failed to start: ${errorMessage}`,
                        },
                        request_id: meetingId,
                        type: 'error',
                        sub_type: isAuthError ? 'authentication' : 'recognition',
                        key: isAuthError ? 'authentication_failed' : 'recognition_failed'
                    });
                }
                // Call onError callback
                if (params.onError) {
                    params.onError(new Error(`Failed to start recognition: ${error}`));
                }
            });
            // 8. Return control interface
            return {
                meetingId,
                stop: () => {
                    isStreaming = false;
                    // Function to clean up media resources
                    const cleanupMediaResources = () => {
                        // Clean up processor node
                        if (processorNode) {
                            processorNode.disconnect();
                        }
                        // Clean up audio context
                        if (audioContext && audioContext.state !== 'closed') {
                            audioContext.close().catch(err => {
                                console.error('Error closing AudioContext:', err);
                            });
                        }
                        // Stop microphone tracks
                        if (micStream) {
                            micStream.getTracks().forEach(track => {
                                track.stop();
                            });
                            micStream = null;
                        }
                        // Stop system audio tracks
                        if (sysStream) {
                            sysStream.getTracks().forEach(track => {
                                track.stop();
                            });
                            sysStream = null;
                        }
                        // Notify of closure
                        if (params.onClose) {
                            params.onClose();
                        }
                        if (params.onEvent) {
                            params.onEvent({
                                message: {
                                    status_update: 'Stream closed and all resources cleaned up',
                                },
                                request_id: meetingId,
                                type: 'info',
                                sub_type: 'stream',
                                key: 'stream_closed'
                            });
                        }
                    };
                    // First stop system and mic streams immediately to ensure they're cleaned up
                    // regardless of recognizer issues
                    if (sysStream) {
                        if (params.onEvent) {
                            params.onEvent({
                                message: {
                                    status_update: 'Stopping system audio capture',
                                },
                                request_id: meetingId,
                                type: 'info',
                                sub_type: 'system_audio',
                                key: 'system_audio_stopping'
                            });
                        }
                        sysStream.getTracks().forEach(track => track.stop());
                    }
                    // Stop recognition
                    if (recognizer) {
                        try {
                            recognizer.stopContinuousRecognitionAsync(() => {
                                // Full cleanup after recognition stops
                                cleanupMediaResources();
                                // Clear reference to recognizer
                                recognizer = null;
                            }, (error) => {
                                if (params.onError) {
                                    params.onError(new Error(`Error stopping recognition: ${error}`));
                                }
                                // Even if recognition stop fails, clean up other resources
                                cleanupMediaResources();
                                // Clear reference to recognizer
                                recognizer = null;
                            });
                        }
                        catch (e) {
                            // Handle any exceptions in stopping recognition
                            if (params.onError) {
                                params.onError(new Error(`Exception stopping recognition: ${e}`));
                            }
                            // Ensure cleanup happens even if there's an exception
                            cleanupMediaResources();
                            recognizer = null;
                        }
                    }
                    else {
                        // If no recognizer, still clean up resources
                        cleanupMediaResources();
                    }
                },
                pause: () => {
                    isStreaming = false;
                    if (recognizer) {
                        recognizer.stopContinuousRecognitionAsync();
                    }
                },
                resume: () => {
                    isStreaming = true;
                    if (recognizer) {
                        recognizer.startContinuousRecognitionAsync();
                    }
                },
                sendMessage: (message) => {
                    // Can be used to send custom messages to the client
                    if (params.onEvent) {
                        params.onEvent({
                            message,
                            request_id: meetingId,
                            type: 'custom',
                            sub_type: 'client_message',
                            key: 'client_message'
                        });
                    }
                }
            };
        }
        catch (error) {
            // Clean up any resources if there's an error
            if (audioContext && audioContext.state !== 'closed') {
                audioContext.close();
            }
            if (micStream) {
                micStream.getTracks().forEach(track => track.stop());
            }
            if (sysStream) {
                sysStream.getTracks().forEach(track => track.stop());
            }
            // Propagate error
            if (params.onError) {
                params.onError(error instanceof Error ? error : new Error(String(error)));
            }
            throw error;
        }
    });
}
// Helper function to generate UUID for meeting ID
function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback implementation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}
